import { Component } from '@theme/component';

/**
 * How long to keep re-asserting the landing after the element connects.
 * @constant {number}
 */
const SETTLE_MS = 2500;

/**
 * Absolute ceiling, so a section that never finishes loading cannot hold the
 * page's scroll position hostage.
 * @constant {number}
 */
const HARD_CAP_MS = 8000;

/**
 * How far the page may move before we read it as the reader scrolling, in px.
 * @constant {number}
 */
const TOLERANCE = 2;

/**
 * Selector for content that is still being fetched and will change the page's
 * height when it arrives.
 * @constant {string}
 */
const PENDING = '.shift-quotes--pending';

/**
 * Events that only a reader can produce. A `scroll` listener cannot be used
 * here — the correction below scrolls the page itself and fires the same event
 * — but none of these can be raised by scrolling anything.
 * @constant {string[]}
 */
const READER_INPUT = ['wheel', 'touchstart', 'pointerdown', 'keydown'];

/**
 * Holds a `#hash` landing on target while the page is still settling.
 *
 * The browser scrolls to the hash once, as soon as the element exists, and
 * never revisits it. On this page that is too early: the comments section
 * replaces its cards with rows fetched from a Google Sheet, and every section
 * below it moves when that lands. A hash aimed at the specs table or the FAQ
 * would be left pointing at whatever the shift pushed into its place.
 *
 * So the landing is re-asserted for a couple of seconds after load, and until
 * the fetched content has arrived.
 *
 * @extends {Component}
 */
export class ShiftAnchorsComponent extends Component {
  /** @type {number | null} */
  #frame = null;

  /** @type {(() => void) | null} */
  #release = null;

  connectedCallback() {
    super.connectedCallback();
    this.#hold();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
  }

  #stop() {
    if (this.#frame != null) cancelAnimationFrame(this.#frame);
    this.#frame = null;
    this.#release?.();
    this.#release = null;
  }

  #hold() {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return;

    const target = document.getElementById(id);
    if (!target) return;

    const start = performance.now();

    // The reader wanting to be somewhere else ends this at once. Two independent
    // signals, because neither is enough on its own:
    //
    //   Input events catch a reader who has started scrolling but barely moved.
    //   The theme sets `html { scroll-behavior: smooth }` globally, so a reader's
    //   scroll is an animation — for the first frames of it the page has hardly
    //   travelled, position alone still reads as "nobody moved", and the
    //   correction below would yank them back mid-gesture.
    //
    //   Position catches everything that moves the page without an input event.
    let released = false;
    const stop = () => {
      released = true;
    };
    for (const type of READER_INPUT) {
      window.addEventListener(type, stop, { capture: true, passive: true, once: true });
    }
    this.#release = () => {
      for (const type of READER_INPUT) window.removeEventListener(type, stop, { capture: true });
    };

    // Where we last put the page, for the position half of the test above.
    /** @type {number | null} */
    let asserted = null;

    /** @param {number} now */
    const tick = (now) => {
      if (released) return this.#stop();
      if (asserted != null && Math.abs(window.scrollY - asserted) > TOLERANCE) return this.#stop();

      // scrollIntoView honours the scroll-margin-top that clears the sticky
      // header, which is why the offset is not repeated here.
      target.scrollIntoView({ block: 'start', behavior: 'instant' });
      asserted = window.scrollY;

      const elapsed = now - start;
      const settling = elapsed < SETTLE_MS || document.querySelector(PENDING) != null;
      if (settling && elapsed < HARD_CAP_MS) this.#frame = requestAnimationFrame(tick);
      else this.#stop();
    };

    this.#frame = requestAnimationFrame(tick);
  }
}

if (!customElements.get('shift-anchors')) {
  customElements.define('shift-anchors', ShiftAnchorsComponent);
}
